import React from 'react';

import { ENV } from '@environment';
import GoogleMapReact from 'google-map-react';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import Carousel from 'components/Carousel';
import HotelStars from 'components/HotelsStars';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import SimpleAlert from 'components/SimpleAlert';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { ConfirmMessage } from 'models/confirmMessage';
import { HotelDetailsCompleteResponse, RoomRatePrice } from 'models/hotelDetailsComplete';
import SolicitationSuccess from 'screens/components/SolicitationSuccess';
import { HotelAmenities } from 'services/hotelService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { htmlT } from 'utils/generic';

import ConfirmApproveModal from '../Dashboard/components/ConfirmApproveModal';
import AboutHotel from './components/AboutHotel';
import Amenities from './components/Amenities';
import BestRoom from './components/BestRoom';
import BookingInfo from './components/BookingInfo';
import MapPin from './components/MapPin';
import RoomList from './components/RoomList';
import {
  Container,
  SubTitle,
  Info,
  Detail,
  MapBox,
  MapBottomAddress,
  Separator,
  RoomsHeader,
  AlertContainer,
} from './styles';

interface Props {
  show: boolean;
  loading: boolean;
  creating: boolean;
  details?: HotelDetailsCompleteResponse;
  onClose: () => void;
  onConfirm: (
    justifyForBreakingRules: string,
    reason: string,
    roomRateId: string,
    roomRate: { refundableType: string },
    roomRatePrice: RoomRatePrice,
    costCenterId?: string,
    breakRulesReasonId?: string,
  ) => void;
  amenitiesCodes: HotelAmenities[];
  showSuccess: boolean;
  traveler?: TravelArrangerUsersAllowed;
  showConfirmFlow: boolean;
  confirmFlowMessage: ConfirmMessage;
  onConfirmFlow: () => void;
  onConfirmFlowClose: () => void;
  setShowConfirmProfile: (value: boolean) => void;
  showConfirmProfile: boolean;
  setShowTravelJustify: (value: boolean) => void;
  showTravelJustify: boolean;
}

const HotelDetails = ({
  show,
  loading,
  creating,
  details,
  onClose,
  onConfirm,
  amenitiesCodes,
  showSuccess,
  traveler,
  onConfirmFlow,
  showConfirmFlow,
  confirmFlowMessage,
  onConfirmFlowClose,
  setShowConfirmProfile,
  showConfirmProfile,
  setShowTravelJustify,
  showTravelJustify,
}: Props) => {
  const { t } = useStoreTranslation();
  const userAccount = useUserAccount();
  const isMobile = useIsMobile();

  if (details !== null && details !== undefined) {
    details.address = details.address.replace('()', '');
  }

  const mapCenter = {
    lat: details?.latitude as number,
    lng: details?.longitude as number,
  };

  const generateFullAddress = () => {
    let address = details?.address;
    if (details?.cityName) address += `, ${details.cityName}`;

    if (details?.stateName) address += `, ${details.stateName}`;

    if (details?.countryName) address += `, ${details.countryName}`;

    return address;
  };

  const fullAddress = generateFullAddress();

  const googleMapsKey = ENV.API_KEYS.GOOGLE_MAPS_API_KEY as string;

  const renderContent = () => {
    if (details) {
      return (
        <>
          {details.images && <Carousel height={200} fit="cover" images={details.images} />}
          <Detail>
            <HotelStars count={5} value={details.category} />
            <SubTitle>{details.hotelName}</SubTitle>
            <Info>{fullAddress}</Info>
          </Detail>
          <Detail>
            <BookingInfo details={details} />
          </Detail>
          <Detail>
            <BestRoom
              rooms={details.rooms}
              checkinDate={details.checkinDate}
              checkoutDate={details.checkoutDate}
              currencyCode={details.currencyCode}
            />
          </Detail>
          <Detail>
            <AboutHotel details={details} />
          </Detail>
          <Detail>
            <Amenities amenities={details.ammenities} amenitiesCodes={amenitiesCodes} />
          </Detail>

          <MapBox>
            <GoogleMapReact
              bootstrapURLKeys={{ key: googleMapsKey }}
              defaultCenter={mapCenter}
              defaultZoom={10}
              options={{ fullscreenControl: false, gestureHandling: 'cooperative' }}
            >
              <MapPin lat={details.latitude} lng={details.longitude} />
            </GoogleMapReact>
            <MapBottomAddress>{details.address}</MapBottomAddress>
          </MapBox>
          <RoomsHeader id="hotel-details-rooms">{t('hotelDetails.selectRoom')}</RoomsHeader>
          <Separator />
          <RoomList
            creating={creating}
            details={details}
            onConfirm={onConfirm}
            bookingDisabled={!userAccount?.bookingAllowed}
            showTravelJustify={showTravelJustify}
            showConfirmProfile={showConfirmProfile}
            setShowConfirmProfile={setShowConfirmProfile}
            setShowTravelJustify={setShowTravelJustify}
          />
          <AlertContainer>
            {!userAccount?.bookingAllowed && (
              <SimpleAlert title={t('bookingNotAllowedAlert.title')} content={t('bookingNotAllowedAlert.content')} />
            )}
          </AlertContainer>
          <SolicitationSuccess show={showSuccess} isHotel={true} isFlight={false} testId="hotel-solicitation-success" />
        </>
      );
    }
    return null;
  };

  return (
    <>
      <ApplicationOverlayContent show={show} width={600} onClose={onClose} testId="hotel-details">
        <Container>
          <Mobile>
            <AppBar goBack={onClose}>{t('hotelDetails.title')}</AppBar>
          </Mobile>

          <Desktop>
            <ModalHeader
              onClose={onClose}
              title={t('hotelDetails.title')}
              subtitle={
                traveler ? (
                  <>{htmlT(t, 'hotelsSearch.toTraveler', { user: `${traveler.name} ${traveler.lastName}` })}</>
                ) : undefined
              }
            />
          </Desktop>

          {loading ? <Loader fullScreen={isMobile} /> : renderContent()}
        </Container>
      </ApplicationOverlayContent>

      <ConfirmApproveModal
        onConfirmApprove={onConfirmFlow}
        onConfirmClose={onConfirmFlowClose}
        showConfirm={showConfirmFlow}
        confirmMessage={confirmFlowMessage}
        showCancelButton={false}
      ></ConfirmApproveModal>
    </>
  );
};

export default HotelDetails;
